// Figma tokens for components/ui, each named `ds-` plus its Figma name, so none collides with an
// existing class. Styles keep their full name (Text/Primary → ds-text-primary); variables keep their
// last segment (spacing/space-05 → ds-space-05). A `-desktop` token is Figma's desktop mode; use
// it with `md:`. The legacy classes these replace are lint errors in components/ui (.eslintrc.js).
// Figma: https://www.figma.com/design/72p7BRSjceW2TlzRIHHRAB/Evvy-Core-Design-System
// px on purpose: the app root is 14px, so rem would not match Figma.
// TODO: Once the app root is 16px, use rem for spacing.

const INTER = '"Inter", sans-serif';
const MARIST = "Marist, serif";

// Figma text styles (Typescale) Each becomes one class, `ds-` plus its style name,
// that sets the font and switches from the mobile to the desktop [size, line height] at md:.
// Figma's letter spacing is a percent of the font size, so -1% is -0.01em.
const TEXT_STYLES = {
  "ds-header-display": {
    fontFamily: INTER,
    fontWeight: "900",
    letterSpacing: "-0.01em",
    mobile: ["35px", "42px"],
    desktop: ["52px", "62px"],
  },
  "ds-header-h1": {
    fontFamily: MARIST,
    fontWeight: "350",
    letterSpacing: "-0.01em",
    mobile: ["40px", "48px"],
    desktop: ["60px", "72px"],
  },
  "ds-header-h2": {
    fontFamily: MARIST,
    fontWeight: "350",
    mobile: ["32px", "38px"],
    desktop: ["40px", "48px"],
  },
  "ds-header-h3": {
    fontFamily: MARIST,
    fontWeight: "350",
    mobile: ["24px", "29px"],
    desktop: ["32px", "38px"],
  },
  "ds-header-h4": {
    fontFamily: MARIST,
    fontWeight: "350",
    mobile: ["20px", "24px"],
    desktop: ["24px", "29px"],
  },
  "ds-header-h5": {
    fontFamily: INTER,
    fontWeight: "600",
    mobile: ["18px", "23px"],
    desktop: ["24px", "31px"],
  },
  "ds-header-h6": {
    fontFamily: INTER,
    fontWeight: "600",
    mobile: ["16px", "19px"],
    desktop: ["20px", "24px"],
  },
  "ds-header-subheader": {
    fontFamily: INTER,
    fontWeight: "600",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    mobile: ["12px", "14px"],
    desktop: ["13px", "16px"],
  },
  "ds-body-large-semibold": {
    fontFamily: INTER,
    fontWeight: "600",
    mobile: ["16px", "22px"],
    desktop: ["18px", "25px"],
  },
  "ds-body-large-medium": {
    fontFamily: INTER,
    fontWeight: "500",
    mobile: ["16px", "22px"],
    desktop: ["18px", "25px"],
  },
  "ds-body-large-regular": {
    fontFamily: INTER,
    fontWeight: "400",
    mobile: ["16px", "22px"],
    desktop: ["18px", "25px"],
  },
  "ds-body-large-serif": {
    fontFamily: MARIST,
    fontWeight: "350",
    mobile: ["16px", "22px"],
    desktop: ["18px", "25px"],
  },
  "ds-body-base-semibold": {
    fontFamily: INTER,
    fontWeight: "600",
    mobile: ["14px", "21px"],
    desktop: ["16px", "24px"],
  },
  "ds-body-base-medium": {
    fontFamily: INTER,
    fontWeight: "500",
    mobile: ["14px", "21px"],
    desktop: ["16px", "24px"],
  },
  "ds-body-base-regular": {
    fontFamily: INTER,
    fontWeight: "400",
    mobile: ["14px", "21px"],
    desktop: ["16px", "24px"],
  },
  "ds-body-small-semibold": {
    fontFamily: INTER,
    fontWeight: "600",
    mobile: ["12px", "18px"],
    desktop: ["14px", "21px"],
  },
  "ds-body-small-medium": {
    fontFamily: INTER,
    fontWeight: "500",
    mobile: ["12px", "18px"],
    desktop: ["14px", "21px"],
  },
  "ds-body-small-regular": {
    fontFamily: INTER,
    fontWeight: "400",
    mobile: ["12px", "18px"],
    desktop: ["14px", "21px"],
  },
  // xSmall is the same size in both modes.
  "ds-body-xsmall-semibold": {
    fontFamily: INTER,
    fontWeight: "600",
    mobile: ["12px", "18px"],
  },
  "ds-body-xsmall-medium": {
    fontFamily: INTER,
    fontWeight: "500",
    mobile: ["12px", "18px"],
  },
  "ds-body-xsmall-regular": {
    fontFamily: INTER,
    fontWeight: "400",
    mobile: ["12px", "18px"],
  },
};

// Components layer, so a utility on the same element (text-[20px], font-semibold) still wins.
const textStyles = ({ addComponents, theme }) =>
  addComponents(
    Object.fromEntries(
      Object.entries(TEXT_STYLES).map(
        ([name, { mobile, desktop, ...font }]) => [
          `.${name}`,
          {
            ...font,
            fontSize: mobile[0],
            lineHeight: mobile[1],
            ...(desktop && {
              [`@media (min-width: ${theme("screens.md")})`]: {
                fontSize: desktop[0],
                lineHeight: desktop[1],
              },
            }),
          },
        ]
      )
    )
  );

module.exports = {
  // Read by cn (tailwind-merge) and the /ui/ gallery. Tailwind ignores this key.
  textStyles: Object.keys(TEXT_STYLES),
  // The values themselves, for the results PDF: react-pdf takes style objects, not classes.
  textStyleValues: TEXT_STYLES,
  plugins: [textStyles],
  theme: {
    extend: {
      // Color styles
      colors: {
        "ds-text-primary": "#11161A",
        "ds-text-base": "#11161A",
        "ds-text-light-on-white": "#707376",
        "ds-background-primary": "#FFFFFF",
        "ds-background-base": "#FFFFFF",
        "ds-background-creme": "#F3F1EE",
        "ds-line-light-on-creme": "#B8B9BA",
        "ds-ui-elements-black": "#11161A",
        "ds-ui-elements-white": "#FFFFFF",
        "ds-ui-elements-evvy-blue": "#6CA7FF",
        "ds-ui-elements-deactivated": "#DBDCDD",
        "ds-ui-elements-neon-yellow": "#E7FB6E",
        "ds-all-colors-electric": "#E7FB6E",
        "ds-all-colors-sky": "#BCCDE3",
        "ds-all-colors-pond": "#1B8064",
        "ds-all-colors-raspberry": "#93113A",
        "ds-text-light-on-creme": "#665F59",
        "ds-line-light-on-white": "#DBDCDD",
        "ds-background-light-creme": "#F9F8F7",
        "ds-background-dark-creme": "#DED7D1",
        "ds-ui-elements-error": "#D91000",
        "ds-ui-elements-dark-green": "#074C4C",
        "ds-ui-elements-light-green": "#1B8064",
        "ds-all-colors-terracotta": "#AD3700",
        // Same hex as ds-ui-elements-dark-green; kept so classes match the Figma style name.
        "ds-all-colors-pine": "#074C4C",
        "ds-all-colors-salmon": "#FFA684",
        "ds-all-colors-orchid": "#C89CD9",
        "ds-all-colors-frog": "#99A866",
        "ds-background-black": "#11161A",
        "ds-text-white": "#FFFFFF",
        "ds-line-black": "#11161A",
        "ds-all-colors-lilac": "#E1D6EA",
      },
      // spacing/space-*
      spacing: {
        "ds-space-01": "2px",
        "ds-space-02": "4px",
        "ds-space-03": "8px",
        "ds-space-04": "12px",
        "ds-space-05": "12px",
        "ds-space-05-desktop": "16px",
        "ds-space-06": "16px",
        "ds-space-06-desktop": "24px",
        "ds-space-07": "24px",
        "ds-space-07-desktop": "32px",
        "ds-space-08": "40px",
        "ds-space-08-desktop": "48px",
        "ds-space-09": "48px",
        "ds-space-09-desktop": "80px",
        // margin/margin-01, the page gutter.
        "ds-margin-01": "16px",
        "ds-margin-01-desktop": "90px",
      },
      // corners/corner-*
      borderRadius: {
        "ds-corner-small": "4px",
        "ds-corner-base": "12px",
        "ds-corner-large": "20px",
        "ds-corner-full": "100px",
        "ds-corner-round": "100px",
      },
      // icons/icon-*
      size: {
        "ds-icon-small": "12px",
        "ds-icon-base": "16px",
        "ds-icon-large": "24px",
        "ds-icon-xlarge": "48px",
      },
      // One mode of a text style, for a component whose text does not switch at md: (CTA mini).
      // Also used, with the -desktop pair, by components not yet on TEXT_STYLES classes (Badge,
      // TextLink, consentDisclosure, providerCaptureQuestion). New code uses the TEXT_STYLES class.
      fontSize: {
        "ds-header-subheader": [
          "12px",
          { lineHeight: "14px", letterSpacing: "0.05em", fontWeight: "600" },
        ],
        "ds-header-subheader-desktop": [
          "13px",
          { lineHeight: "16px", letterSpacing: "0.05em", fontWeight: "600" },
        ],
        // The Fertility-V My Plan hero keeps the mobile h1 on desktop.
        "ds-header-h1": [
          "40px",
          { lineHeight: "48px", letterSpacing: "-0.01em", fontWeight: "350" },
        ],
        "ds-header-h3": ["24px", { lineHeight: "1.2", fontWeight: "400" }],
        "ds-header-h3-desktop": [
          "32px",
          { lineHeight: "1.2", fontWeight: "400" },
        ],
        "ds-body-xsmall-medium": [
          "12px",
          { lineHeight: "18px", fontWeight: "500" },
        ],
        "ds-body-small-medium": [
          "12px",
          { lineHeight: "18px", fontWeight: "500" },
        ],
        "ds-body-small-medium-desktop": [
          "14px",
          { lineHeight: "21px", fontWeight: "500" },
        ],
        "ds-body-small-regular": [
          "12px",
          { lineHeight: "18px", fontWeight: "400" },
        ],
        "ds-body-small-regular-desktop": [
          "14px",
          { lineHeight: "21px", fontWeight: "400" },
        ],
        "ds-body-xsmall-regular": [
          "12px",
          { lineHeight: "18px", fontWeight: "400" },
        ],
        "ds-body-base-regular": [
          "14px",
          { lineHeight: "21px", fontWeight: "400" },
        ],
        "ds-body-base-regular-desktop": [
          "16px",
          { lineHeight: "24px", fontWeight: "400" },
        ],
        "ds-body-base-medium": [
          "14px",
          { lineHeight: "21px", fontWeight: "500" },
        ],
        "ds-body-base-medium-desktop": [
          "16px",
          { lineHeight: "24px", fontWeight: "500" },
        ],
        "ds-body-base-semibold": [
          "14px",
          { lineHeight: "21px", fontWeight: "600" },
        ],
        "ds-body-base-semibold-desktop": [
          "16px",
          { lineHeight: "24px", fontWeight: "600" },
        ],
      },
      // Effect style Shadow/01 (Shadow/02 has the same values). Figma's blur 12 renders as CSS blur(6px).
      boxShadow: {
        "ds-shadow-01":
          "1px 1px 2px 0 rgba(112, 115, 118, 0.2), inset 1px 1px 3px 0 rgba(255, 255, 255, 0.5)",
      },
      backdropBlur: {
        "ds-shadow-01": "6px",
      },
    },
  },
};
